import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type {
  ClinicianUpgradeCheckoutAction,
  ClinicianUpgradeCheckoutSelection,
  ComplimentaryTrialEndAnalyticsContext,
  ComplimentaryTrialEndBannerAction,
  ComplimentaryTrialEndEntryPoint,
  ComplimentaryTrialEndModalAction,
} from '@heidi/commerce/contracts';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { BillingSeatType, SubscriptionTier } from '@heidi/schema/ml-scribe';

const TRACK_OPTIONS = { amplitude: false } as const;

const BANNER_ACTIONS = {
  keep_usage: ButtonAction.Heidi2TrialEndBannerKeepUsage,
  give_task: ButtonAction.Heidi2TrialEndBannerGiveTask,
  dismiss: ButtonAction.Heidi2TrialEndBannerDismiss,
} satisfies Record<ComplimentaryTrialEndBannerAction, ButtonAction>;

const MODAL_ACTIONS = {
  get_clinician_max: ButtonAction.Heidi2TrialEndModalGetClinicianMax,
  view_all_plans: ButtonAction.Heidi2TrialEndModalViewAllPlans,
} satisfies Record<ComplimentaryTrialEndModalAction, ButtonAction>;

const viewedProps = ({
  daysRemaining,
  usagePercent,
  cohort,
}: ComplimentaryTrialEndAnalyticsContext) => ({
  days_remaining: daysRemaining,
  usage_percent: usagePercent,
  is_low_usage: cohort === 'inactive',
});

const clickedProps = ({
  daysRemaining,
  usagePercent,
}: ComplimentaryTrialEndAnalyticsContext) => ({
  days_remaining: daysRemaining,
  usage_percent: usagePercent,
});

/** The strip banner is a nudge surface, so it reports as `modal_viewed` like the side-nav popover. */
export function trackComplimentaryTrialEndBannerViewed(
  context: ComplimentaryTrialEndAnalyticsContext
): void {
  track({
    options: TRACK_OPTIONS,
    event_name: TrackingEventName.ModalViewed,
    props: {
      screen_name: ScreenName.Heidi2TrialEndBanner,
      ...viewedProps(context),
    },
  });
}

export function trackComplimentaryTrialEndBannerClick({
  action,
  label,
  ...context
}: ComplimentaryTrialEndAnalyticsContext & {
  action: ComplimentaryTrialEndBannerAction;
  label: string;
}): void {
  track({
    options: TRACK_OPTIONS,
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Heidi2TrialEndBanner,
      action: BANNER_ACTIONS[action],
      label,
      ...clickedProps(context),
    },
  });
}

export function trackComplimentaryTrialEndModalViewed({
  entryPoint,
  ...context
}: ComplimentaryTrialEndAnalyticsContext & {
  entryPoint: ComplimentaryTrialEndEntryPoint;
}): void {
  track({
    options: TRACK_OPTIONS,
    event_name: TrackingEventName.ModalViewed,
    props: {
      screen_name: ScreenName.Heidi2TrialEndModal,
      entry_point: entryPoint,
      ...viewedProps(context),
    },
  });
}

export function trackComplimentaryTrialEndModalClick({
  action,
  label,
  entryPoint,
  ...context
}: ComplimentaryTrialEndAnalyticsContext & {
  action: ComplimentaryTrialEndModalAction;
  label: string;
  entryPoint: ComplimentaryTrialEndEntryPoint;
}): void {
  track({
    options: TRACK_OPTIONS,
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Heidi2TrialEndModal,
      action: MODAL_ACTIONS[action],
      label,
      entry_point: entryPoint,
      ...clickedProps(context),
    },
  });
}

const CHECKOUT_ACTIONS = {
  continue: ButtonAction.Heidi2TrialEndCheckoutContinue,
  cancel: ButtonAction.Heidi2TrialEndCheckoutCancel,
} satisfies Record<ClinicianUpgradeCheckoutAction, ButtonAction>;

export function trackClinicianUpgradeCheckoutViewed(): void {
  track({
    options: TRACK_OPTIONS,
    event_name: TrackingEventName.ModalViewed,
    props: {
      screen_name: ScreenName.Heidi2TrialEndCheckout,
      target_plan: SubscriptionTier.CLINICIAN_PRO,
    },
  });
}

export function trackClinicianUpgradeCheckoutClick({
  action,
  label,
  seat,
  period,
}: ClinicianUpgradeCheckoutSelection & {
  action: ClinicianUpgradeCheckoutAction;
  label: string;
}): void {
  track({
    options: TRACK_OPTIONS,
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Heidi2TrialEndCheckout,
      action: CHECKOUT_ACTIONS[action],
      label,
      target_plan: SubscriptionTier.CLINICIAN_PRO,
      target_seat_type: seat === 'max' ? BillingSeatType.MAX : BillingSeatType.STANDARD,
      period,
    },
  });
}
